import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { Input } from '@/components/ui/Input';
import {
  createShares,
  SHARE_SECTION_CEILINGS,
  type CreateSharesPayload,
  type OpsPlanShareSection,
  type OpsPlanSectionsConfig,
  type ShareRecipientType,
} from '@/services/opsPlanShares.service';

interface ShareOpsPlanModalProps {
  activityId: string;
  activityEnd?: string | null;
  attachments: Array<{ id: string; originalFilename: string }>;
  principalEmails: Array<{ name: string; email: string }>;
  isOpen: boolean;
  onClose: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function buildDefaultSections(
  recipientType: ShareRecipientType
): Partial<Record<OpsPlanShareSection, boolean>> {
  const ceiling = SHARE_SECTION_CEILINGS[recipientType];
  const result: Partial<Record<OpsPlanShareSection, boolean>> = {};
  for (const [key, meta] of Object.entries(ceiling) as [OpsPlanShareSection, { defaultOn: boolean; label: string }][]) {
    result[key] = meta.defaultOn;
  }
  return result;
}

export default function ShareOpsPlanModal({
  activityId,
  activityEnd,
  attachments,
  principalEmails,
  isOpen,
  onClose,
}: ShareOpsPlanModalProps) {
  const queryClient = useQueryClient();

  const [recipientType, setRecipientType] = useState<ShareRecipientType>('CONTRACTOR');
  const [recipients, setRecipients] = useState<Array<{ name: string; email: string }>>([]);
  const [sections, setSections] = useState<Partial<Record<OpsPlanShareSection, boolean>>>(
    buildDefaultSections('CONTRACTOR')
  );
  const [selectedAttachments, setSelectedAttachments] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [mutationError, setMutationError] = useState('');

  const ceiling = SHARE_SECTION_CEILINGS[recipientType];

  const effectiveSections = useMemo<OpsPlanSectionsConfig>(() => {
    const result: OpsPlanSectionsConfig = {};
    for (const [key, meta] of Object.entries(ceiling) as [OpsPlanShareSection, { defaultOn: boolean; label: string }][]) {
      result[key] = sections[key] ?? meta.defaultOn;
    }
    return result;
  }, [ceiling, sections]);

  const handleTypeChange = (type: ShareRecipientType) => {
    setRecipientType(type);
    setSections(buildDefaultSections(type));
    if (type === 'EXECUTIVE') {
      const valid = principalEmails.filter((p) => EMAIL_RE.test(p.email));
      setRecipients(valid);
    } else {
      setRecipients([]);
    }
    setEmailError('');
  };

  const handleAddRecipient = () => {
    const trimmedName = newName.trim();
    const trimmedEmail = newEmail.trim().toLowerCase();

    if (!trimmedName) {
      setEmailError('Name is required');
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setEmailError('Enter a valid email address');
      return;
    }
    if (recipients.some((r) => r.email.toLowerCase() === trimmedEmail)) {
      setEmailError('This email is already in the list');
      return;
    }

    setRecipients((prev) => [...prev, { name: trimmedName, email: trimmedEmail }]);
    setNewName('');
    setNewEmail('');
    setEmailError('');
  };

  const handleRemoveRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((r) => r.email !== email));
  };

  const handleSectionToggle = (section: OpsPlanShareSection) => {
    setSections((prev) => ({
      ...prev,
      [section]: !(prev[section] ?? ceiling[section]?.defaultOn),
    }));
  };

  const handleAttachmentToggle = (id: string) => {
    setSelectedAttachments((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const formattedActivityEnd = activityEnd
    ? new Date(activityEnd).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  const createMutation = useMutation({
    mutationFn: (payload: CreateSharesPayload) => createShares(activityId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ops-plan-shares', activityId] });
      onClose();
    },
    onError: (err: unknown) => {
      setMutationError(err instanceof Error ? err.message : 'Failed to send shares');
    },
  });

  const handleSubmit = () => {
    setMutationError('');
    const payload: CreateSharesPayload = {
      recipientType,
      recipients,
      sectionsConfig: effectiveSections,
      attachmentIds: selectedAttachments,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
    };
    createMutation.mutate(payload);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal card */}
      <div className="relative z-10 w-full max-w-lg bg-white dark:bg-secondary-900 rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Share Operations Plan
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
          {/* Recipient type toggle */}
          <div>
            <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
              Recipient type
            </p>
            <div className="flex rounded-lg border border-secondary-300 dark:border-secondary-600 overflow-hidden w-fit">
              {(['CONTRACTOR', 'EXECUTIVE'] as ShareRecipientType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => handleTypeChange(type)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    recipientType === type
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                  }`}
                >
                  {type === 'CONTRACTOR' ? 'Contractor' : 'Executive'}
                </button>
              ))}
            </div>
          </div>

          {/* Recipients */}
          <div>
            <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
              Recipients
            </p>
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {recipients.map((r) => (
                  <span
                    key={r.email}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                  >
                    {r.name} &lt;{r.email}&gt;
                    <button
                      onClick={() => handleRemoveRecipient(r.email)}
                      className="ml-1 rounded-full hover:text-primary-600 dark:hover:text-primary-200 transition-colors"
                      aria-label={`Remove ${r.email}`}
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add recipient form */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { handleAddRecipient(); } }}
                  size="sm"
                />
              </div>
              <div className="flex-1">
                <Input
                  placeholder="Email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { handleAddRecipient(); } }}
                  variant={emailError ? 'error' : 'default'}
                  size="sm"
                />
              </div>
              <Button size="sm" variant="secondary" onClick={handleAddRecipient}>
                Add
              </Button>
            </div>
            {emailError && (
              <p className="mt-1 text-xs text-danger-600 dark:text-danger-400">{emailError}</p>
            )}
          </div>

          {/* Section toggles */}
          <div>
            <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
              Sections to share
            </p>
            <div className="space-y-2">
              {(Object.entries(ceiling) as [OpsPlanShareSection, { defaultOn: boolean; label: string }][]).map(([key, meta]) => (
                <label
                  key={key}
                  className="flex items-center gap-2 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={sections[key] ?? meta.defaultOn}
                    onChange={() => handleSectionToggle(key)}
                    className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-800"
                  />
                  <span className="text-sm text-secondary-700 dark:text-secondary-300">
                    {meta.label}
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-secondary-500 dark:text-secondary-400 italic">
              Principal personal data (medical, home address, contacts) is never shared.
            </p>
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div>
              <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Attachments
              </p>
              <div className="space-y-2">
                {attachments.map((att) => (
                  <label key={att.id} className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={selectedAttachments.includes(att.id)}
                      onChange={() => handleAttachmentToggle(att.id)}
                      className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-800"
                    />
                    <span className="text-sm text-secondary-700 dark:text-secondary-300 truncate">
                      {att.originalFilename}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Expiry */}
          <div>
            <DateTimeInput
              label="Link expiry (optional)"
              value={expiresAt}
              onChange={setExpiresAt}
            />
            <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
              Default: 24h after the activity ends
              {formattedActivityEnd ? ` (${formattedActivityEnd})` : ''}. Maximum 30 days.
            </p>
          </div>

          {/* Error */}
          {mutationError && (
            <p className="text-sm text-danger-600 dark:text-danger-400">{mutationError}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50 rounded-b-xl">
          <Button variant="outline" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={recipients.length === 0 || createMutation.isPending}
            loading={createMutation.isPending}
          >
            Send to {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}
